import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import { ArticleBody } from "@/components/ArticleBody";
import { API } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL, ogFromAsset } from "@/lib/seo";
import blogGenerated from "@/data/blog.generated.json";

// Build-time snapshot of every published post. Used to hydrate the initial state
// so the react-snap crawler always sees the article HTML — the runtime fetch cannot
// reach the public preview URL from headless Chromium (Cloudflare loop protection).
const BLOG_SEED = (blogGenerated && blogGenerated.posts) || [];
const seedByKey = (key) => BLOG_SEED.find((p) => p.slug === key || p.id === key) || null;

export default function BlogPostPage() {
  const { id } = useParams();
  const [post, setPost] = useState(() => seedByKey(id));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => {
        // Only surface "not found" if the seed didn't already give us the post
        if (!seedByKey(id)) setNotFound(true);
      });
  }, [id]);

  useSeo({
    title: post ? `${post.title}` : undefined,
    description: post?.excerpt || undefined,
    canonical: post?.slug ? `${SITE_URL}/blog/${post.slug}` : undefined,
    ogImage: post?.image ? (post.image.startsWith("/assets/") ? ogFromAsset(post.image) : post.image) : undefined,
    ogImageAlt: post?.title,
  });

  // Old UUID URLs (or any :id that doesn't match the canonical slug) redirect
  // to the pretty slug BEFORE rendering any content so Googlebot always lands
  // on the canonical URL.
  if (post?.slug && id !== post.slug) {
    return <Navigate to={`/blog/${post.slug}`} replace />;
  }

  if (notFound) {
    return (
      <div>
        <Header />
        <main className="section-pad text-center">
          <h1 className="font-display font-black text-3xl">Artículo no encontrado</h1>
          <Link to="/blog" className="btn-pill btn-secondary mt-6 inline-flex">Volver al blog</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) return <div className="p-10 text-center">Cargando...</div>;

  return (
    <div data-testid="blog-post-page">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        dateModified: post.updated_at || post.date,
        inLanguage: "es-ES",
        author: { "@type": "Organization", name: "Autoescuelas Faro", url: SITE_URL },
        publisher: { "@type": "Organization", name: "Autoescuelas Faro", url: SITE_URL },
        ...(post.image ? { image: post.image.startsWith("http") ? post.image : `${SITE_URL}${post.image}` } : {}),
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          { "@type": "ListItem", position: 3, name: post.title },
        ],
      }} />
      <Header />
      <main className="bg-white">
        <ArticleBody post={post} />
      </main>
      <Footer />
      <FloatingWhatsapp />
    </div>
  );
}
